import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { createBuyerInterest } from "@/lib/api";
import { CheckCircle2, ArrowRight, Truck, Globe2, ShoppingBag } from "lucide-react";

const HERO = "https://images.pexels.com/photos/5945743/pexels-photo-5945743.jpeg?auto=compress&w=1600";

export default function Buyers() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    company_name: "", contact_person: "", phone: "", email: "",
    buyer_type: "wholesale", city: "", country: "India",
    commodities_of_interest: "Jalna Sweet Lime (Mosambi)",
    monthly_volume: "", notes: "", consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_person || !form.phone || !form.email || !form.city) {
      toast.error(t("toast.fill_required"));
      return;
    }
    if (!form.consent) { toast.error(t("buyer.consent_required")); return; }
    setSubmitting(true);
    try {
      await createBuyerInterest({
        company_name: form.company_name,
        contact_person: form.contact_person,
        phone: form.phone.replace(/\D/g, "").slice(-10),
        email: form.email,
        buyer_type: form.buyer_type,
        city: form.city,
        country: form.country,
        commodities_of_interest: form.commodities_of_interest.split(",").map((s) => s.trim()).filter(Boolean),
        monthly_volume: form.monthly_volume || null,
        notes: form.notes || null,
        consent: true,
        preferred_language: "en",
      });
      setDone(true);
      toast.success(t("toast.buyer_ok"));
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error || t("toast.generic_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-buyers">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Fresh limes" className="w-full h-full object-cover" />
          <div className="absolute inset-0 tr-hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-28 lg:py-40 text-white">
          <div className="tr-eyebrow !text-[var(--tr-citrus-yellow)] before:!bg-[var(--tr-citrus-yellow)]">{t("buyer.hero_eyebrow")}</div>
          <h1 className="tr-rise mt-4 font-display font-extrabold text-5xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-tight max-w-4xl">
            {t("buyer.hero_title_a")}<br />{t("buyer.hero_title_b")} <span className="text-[var(--tr-citrus-yellow)]">{t("buyer.hero_title_c")}</span> {t("buyer.hero_title_d")}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/85">{t("buyer.hero_body")}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { icon: ShoppingBag, k: "retail" },
          { icon: Truck, k: "bulk" },
          { icon: Globe2, k: "export" },
        ].map((b) => (
          <div key={b.k} className="tr-card p-8">
            <b.icon className="w-6 h-6 text-[var(--tr-zest)]" />
            <div className="mt-4 font-display font-extrabold text-xl text-[var(--tr-deep-green)]">{t(`buyer.b_${b.k}_t`)}</div>
            <p className="mt-2 text-[var(--tr-muted)] leading-relaxed">{t(`buyer.b_${b.k}_d`)}</p>
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="tr-eyebrow">{t("buyer.form_eyebrow")}</div>
          <h2 className="tr-section-title text-4xl lg:text-5xl mt-4">{t("buyer.form_title_a")}<br />{t("buyer.form_title_b")}</h2>
          <p className="mt-6 text-[var(--tr-muted)] leading-relaxed">{t("buyer.form_body")}</p>
        </div>
        <div className="lg:col-span-7">
          {done ? (
            <div data-testid="buyer-success" className="tr-card p-10 text-center">
              <CheckCircle2 className="w-14 h-14 text-[var(--tr-lime)] mx-auto" />
              <h3 className="font-display font-extrabold text-3xl mt-4 text-[var(--tr-deep-green)]">{t("buyer.success_title")}</h3>
              <p className="mt-3 text-[var(--tr-muted)]">{t("buyer.success_body")}</p>
            </div>
          ) : (
            <form data-testid="buyer-form" onSubmit={submit} className="tr-card p-8 lg:p-10 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label={`${t("buyer.f_company")}*`}><input required data-testid="buyer-company" className="tr-input" value={form.company_name} onChange={onChange("company_name")} /></Field>
                <Field label={`${t("buyer.f_contact")}*`}><input required data-testid="buyer-contact" className="tr-input" value={form.contact_person} onChange={onChange("contact_person")} /></Field>
                <Field label={`${t("buyer.f_phone")}*`}><input required inputMode="tel" data-testid="buyer-phone" className="tr-input" value={form.phone} onChange={onChange("phone")} /></Field>
                <Field label={`${t("buyer.f_email")}*`}><input required type="email" data-testid="buyer-email" className="tr-input" value={form.email} onChange={onChange("email")} /></Field>
                <Field label={t("buyer.f_type")}>
                  <select data-testid="buyer-type" className="tr-input" value={form.buyer_type} onChange={onChange("buyer_type")}>
                    <option value="retail">{t("buyer.type_retail")}</option>
                    <option value="wholesale">{t("buyer.type_wholesale")}</option>
                    <option value="horeca">{t("buyer.type_horeca")}</option>
                    <option value="export">{t("buyer.type_export")}</option>
                    <option value="processor">{t("buyer.type_processor")}</option>
                    <option value="other">{t("buyer.type_other")}</option>
                  </select>
                </Field>
                <Field label={`${t("buyer.f_city")}*`}><input required data-testid="buyer-city" className="tr-input" value={form.city} onChange={onChange("city")} /></Field>
                <Field label={t("buyer.f_country")}><input data-testid="buyer-country" className="tr-input" value={form.country} onChange={onChange("country")} /></Field>
                <Field label={t("buyer.f_volume")}><input data-testid="buyer-volume" className="tr-input" placeholder={t("buyer.f_volume_ph")} value={form.monthly_volume} onChange={onChange("monthly_volume")} /></Field>
                <Field label={t("buyer.f_commodities")} className="md:col-span-2">
                  <input data-testid="buyer-commodities" className="tr-input" value={form.commodities_of_interest} onChange={onChange("commodities_of_interest")} />
                </Field>
                <Field label={t("buyer.f_notes")} className="md:col-span-2">
                  <textarea data-testid="buyer-notes" className="tr-input tr-textarea" value={form.notes} onChange={onChange("notes")} />
                </Field>
              </div>
              {/* DPDP Act consent — Round 3 Part B §3. Unticked by default, required. */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="buyer-consent"
                  checked={form.consent}
                  onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                  className="mt-1 w-5 h-5 accent-[var(--tr-deep-green)] flex-shrink-0"
                />
                <span className="text-sm text-[var(--tr-deep-green)] leading-relaxed">{t("buyer.f_consent")}</span>
              </label>
              <button type="submit" data-testid="buyer-submit" disabled={submitting} className="tr-pill-btn tr-btn-zest disabled:opacity-60">
                {submitting ? t("common.submitting") : t("buyer.f_submit")} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="tr-label">{label}</span>
      {children}
    </label>
  );
}
